import React, { useCallback } from "react";
//#region types
import { TodoCardProps } from "models/propTypes/todoPropTypes";
import { TranslationKeys } from "translations";
import { FormHandles } from "models/propTypes/genericFormProps";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { InvoiceFormTypes } from "models/formTypes";
import { InvoiceTodoView } from "models/propTypes/invoicePropTypes";
//endregion
//#region components
import TopTodoCard from "../../_partials/topTodoCard";
import { EntityCardContent, TitledContainerInner, Skeletons, Profiles, GenericCommentsView } from "components/common";
import {
  InvoiceFooter,
  InvoiceTodoSelectionFormContainer,
  useInvoiceFileViewer,
  useInvoiceSnoozeAction,
} from "components/invoice";
import { useLocation, useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { useInvoiceService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const InvoiceTodoDraft: React.FC<TodoCardProps> = (props) => {
  const invoiceService = useInvoiceService();
  const invoice = props.item.invoice;

  const formRef = React.useRef<FormHandles>(null);

  const [overrideInvoiceDate, setOverrideInvoiceDate] = React.useState<Date>();
  const [overrideHtml, setOverrideHtml] = React.useState<string>();
  const [view, setView] = React.useState<InvoiceTodoView>();
  const [sendType, setSendType] = React.useState<WintSuperkollAPIModel.InvoiceSendMethod | undefined>(undefined);

  const location = useLocation();
  const navigate = useNavigate();

  const snoozeAction = useInvoiceSnoozeAction(invoice!.id, props.onActionSuccess);
  const { fileViewerComponent } = useInvoiceFileViewer(invoice!, true, overrideHtml);

  const invoiceDate = overrideInvoiceDate ?? invoice?.postingDate;

  const onInvoiceDateChanged = (date: Date) => {
    setOverrideInvoiceDate(date);
  };

  const onToggleFinalApproval = async (show: boolean) => {
    setView(show ? InvoiceTodoView.PreviewForm : InvoiceTodoView.BaseForm);
  };

  const onInvoiceInitialValidationChange = (isValid: boolean) => {
    setView(isValid ? InvoiceTodoView.BaseForm : InvoiceTodoView.None);
  };

  const onSubmitForm = async () => {
    return (formRef.current && formRef.current.submit()) || new Promise<void>((resolve) => resolve());
  };

  const onViewInfoClick = () => {
    navigate(UrlHelper.formatPath(`/Invoice/${invoice?.id}`), {
      state: { background: location },
    });
  };

  const loadInvoiceTemplate = useDebouncedCallback((values: InvoiceFormTypes.Todo) => {
    if (invoice && values) {
      const invoicePost: WintSuperkollAPIModel.InvoicePreviewRequest = invoiceService.convertInvoicePutToPreviewRequest(
        { ...invoiceService.convertTodoFormToAPIRequest(values, invoice!), notes: values.notes },
        invoice.fromRecurring,
        invoice.recurringInvoiceVersion,
        invoice.recurringId
      );

      invoicePost.serialNumber = invoice.serialNumber;
      invoicePost.oCRText = invoice.ocrText;

      invoiceService.generateInvoicePreview(invoicePost).then((res) => {
        setOverrideHtml(res.data as unknown as string);
      });
    }
  }, 500);

  const reloadInvoiceTemplate = useCallback(
    (values: InvoiceFormTypes.Todo) => {
      loadInvoiceTemplate(values);
    },
    [loadInvoiceTemplate]
  );

  const onInvoiceSendMethodSelected = (sendType?: WintSuperkollAPIModel.InvoiceSendMethod) => {
    setSendType(sendType);
  };

  return invoice ? (
    <EntityCardContent
      topTodoCard={
        <TopTodoCard
          item={props.item}
          descriptionHeadingOne={LocalizationHelperInstance.getFormattedCurrency(
            invoice?.totalAmount ?? 0,
            invoice?.currency
          )}
          descriptionHeadingTwo={
            invoice?.customerName ? (
              <Profiles.CustomerProfileLink
                id={invoice.customerId}
                name={invoice.customerName ?? ""}
                className={cssExports.titleLink}
              />
            ) : (
              LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Customer.NoName)
            )
          }
          descriptionheadingThree={
            invoice?.ownerName
              ? `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.CreatedBy)} : ${
                  invoice?.ownerName
                }`
              : ""
          }
          footerText={`${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.InvoiceDate)}: ${
            invoiceDate ? LocalizationHelperInstance.getFormattedDate(invoiceDate) : ""
          }`}
          onViewAllClick={onViewInfoClick}
          quickActions={[snoozeAction]}
        />
      }
      todoMainSection={
        <>
          {view === undefined ? <Skeletons.TitledContainerSkeleton /> : <></>}
          <TitledContainerInner
            header={
              view === undefined
                ? undefined
                : LocalizationHelperInstance.getFormattedMessage(
                    view !== InvoiceTodoView.None
                      ? TranslationKeys.Invoice.DraftTodoCompleteHeader
                      : TranslationKeys.Invoice.DraftTodoIncompleteHeader
                  )
            }
            subHeader={
              view === undefined
                ? undefined
                : LocalizationHelperInstance.getFormattedMessage(
                    view !== InvoiceTodoView.None
                      ? TranslationKeys.Invoice.DraftTodoCompleteDescription
                      : TranslationKeys.Invoice.DraftTodoIncompleteDescription
                  )
            }
          >
            <InvoiceTodoSelectionFormContainer
              ref={formRef}
              invoice={invoice}
              invoiceDateChangeHandler={onInvoiceDateChanged}
              view={view}
              onFormUpdate={reloadInvoiceTemplate}
              onInvoiceInitialValidationChange={onInvoiceInitialValidationChange}
              onInvoiceSendMethodSelected={onInvoiceSendMethodSelected}
            />
          </TitledContainerInner>
        </>
      }
      footer={
        <InvoiceFooter
          invoice={invoice}
          sendType={sendType}
          key={invoice.id}
          hideSave
          isComplete={view !== InvoiceTodoView.None}
          isFinalPreview={view === InvoiceTodoView.PreviewForm}
          onSendInvoice={onSubmitForm}
          onShowFinalApproval={onToggleFinalApproval.bind(null, true)}
          onShowReview={onToggleFinalApproval.bind(null, false)}
          markAsDoneHandler={props.onActionSuccess}
          disableSuccessMessages
        />
      }
      infoViewer={fileViewerComponent}
    >
      {view !== InvoiceTodoView.PreviewForm ? (
        <GenericCommentsView parentId={invoice.id} type={WintSuperkollAPIModel.GenericCommentType.Invoice} />
      ) : (
        <></>
      )}
    </EntityCardContent>
  ) : (
    <></>
  );
};

export default InvoiceTodoDraft;
